import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import Icon from '../common/containers/icon';
import getPageInfo from './getPageInfo';
import FavoriteTitlePromptModal from './favoriteTitlePromptModal';
import { addFavoriteWithValidation, FAVORITES_UNAVAILABLE_USER_MESSAGE, FavoritesUnavailableError, findFavoriteByPageUrl, getPinnedFavoritesFromPreferencesContent, isPageUrlFavorited, prepareAddFavorite } from './favoritesHelpers';
import { getError } from '../../actions/ajax';
import entityComposition from '../common/classes/entityComposition';
import { getPreferencesContent, useUserPreferencesFavorites } from './userPreferencesFavorites';
const FavoriteButton = _ref => {
  var _form$active;
  let _ref$notificationActi = _ref.notificationActions,
    notificationActions = _ref$notificationActi === void 0 ? {} : _ref$notificationActi,
    _ref$form = _ref.form,
    form = _ref$form === void 0 ? {} : _ref$form,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'sm' : _ref$size;
  const location = useLocation();
  const _useUserPreferencesFa = useUserPreferencesFavorites(),
    preferencesContent = _useUserPreferencesFa.preferencesContent,
    loading = _useUserPreferencesFa.loading,
    ensureLoaded = _useUserPreferencesFa.ensureLoaded,
    upsertPinnedFavorite = _useUserPreferencesFa.upsertPinnedFavorite,
    removePinnedFavorite = _useUserPreferencesFa.removePinnedFavorite;
  const formTitle = (form === null || form === void 0 || (_form$active = form.active) === null || _form$active === void 0 || (_form$active = _form$active.content) === null || _form$active === void 0 ? void 0 : _form$active.title) || null;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showTitlePrompt = _useState2[0],
    setShowTitlePrompt = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    customTitle = _useState4[0],
    setCustomTitle = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    closeModalSignal = _useState6[0],
    setCloseModalSignal = _useState6[1];
  const titleInputRef = useRef(null);
  const currentUrl = "".concat(location.pathname).concat(location.search);
  const getCurrentPageInfo = useCallback(() => getPageInfo(formTitle || null), [formTitle]);
  const favorites = getPinnedFavoritesFromPreferencesContent(preferencesContent);
  const isFavorited = !loading && isPageUrlFavorited(favorites, currentUrl);
  const persistPinnedFavorite = useCallback(favorite => upsertPinnedFavorite(favorite), [upsertPinnedFavorite]);
  const markFavoriteAsRemoved = useCallback(async pinId => removePinnedFavorite(pinId), [removePinnedFavorite]);
  useEffect(() => {
    ensureLoaded().catch(() => {});
  }, [ensureLoaded, location.pathname, location.search]);
  useEffect(() => {
    if (showTitlePrompt && titleInputRef.current) {
      setTimeout(() => {
        var _titleInputRef$curren;
        (_titleInputRef$curren = titleInputRef.current) === null || _titleInputRef$curren === void 0 || _titleInputRef$curren.focus();
      }, 100);
    }
  }, [showTitlePrompt]);
  useEffect(() => {
    if (closeModalSignal === 0) {
      return;
    }
    setShowTitlePrompt(false);
    setCustomTitle('');
    setCloseModalSignal(0);
  }, [closeModalSignal]);
  const handleTitleCancel = useCallback(() => {
    setShowTitlePrompt(false);
    setCustomTitle('');
  }, []);
  const handleTitleSubmit = useCallback(async () => {
    const trimmedTitle = customTitle.trim();
    if (!trimmedTitle) {
      return;
    }
    try {
      const added = await addFavoriteWithValidation({
        persistPinnedFavorite,
        getPageInfo: getCurrentPageInfo,
        title: trimmedTitle,
        form,
        notificationActions
      });
      if (added === null) {
        return;
      }
      setShowTitlePrompt(false);
      setCustomTitle('');
      setCloseModalSignal(s => s + 1);
    } catch (err) {
      const _getError = getError(err),
        errorMsg = _getError.error_msg;
      if (notificationActions !== null && notificationActions !== void 0 && notificationActions.error) {
        notificationActions.error({
          headerMessage: 'Failed to add favorite',
          message: errorMsg || 'Please try again.',
          id: 'favorite-add-failed'
        });
      }
    }
  }, [customTitle, persistPinnedFavorite, getCurrentPageInfo, form, notificationActions]);
  const handleToggleFavorite = async () => {
    await ensureLoaded().catch(() => {});
    const loadedFavorites = getPinnedFavoritesFromPreferencesContent(getPreferencesContent());
    const pageUrl = "".concat(location.pathname).concat(location.search);
    const isFavoritedNow = isPageUrlFavorited(loadedFavorites, pageUrl);
    if (isFavoritedNow) {
      const favoriteToRemove = findFavoriteByPageUrl(loadedFavorites, pageUrl);
      if (favoriteToRemove) {
        try {
          const removed = await markFavoriteAsRemoved(favoriteToRemove.id);
          if (!removed) {
            return;
          }
          if (notificationActions !== null && notificationActions !== void 0 && notificationActions.info) {
            const currentPageInfo = getCurrentPageInfo();
            notificationActions.info({
              headerMessage: 'Favorite Removed',
              message: "\"".concat(favoriteToRemove.label || currentPageInfo.title, "\" has been removed from your favorites."),
              id: 'favorite-removed'
            });
          }
        } catch (err) {
          if (err instanceof FavoritesUnavailableError) {
            if (notificationActions !== null && notificationActions !== void 0 && notificationActions.error) {
              notificationActions.error({
                headerMessage: 'Failed to remove favorite',
                message: FAVORITES_UNAVAILABLE_USER_MESSAGE,
                id: 'favorite-remove-unavailable'
              });
            }
            return;
          }
          const _getError2 = getError(err),
            errorMsg = _getError2.error_msg;
          if (notificationActions !== null && notificationActions !== void 0 && notificationActions.error) {
            notificationActions.error({
              headerMessage: 'Failed to remove favorite',
              message: errorMsg || 'Please try again.',
              id: 'favorite-remove-failed'
            });
          }
        }
      }
      return;
    }
    const _prepareAddFavorite = prepareAddFavorite({
        getPageInfo: getCurrentPageInfo,
        form,
        notificationActions
      }),
      canProceed = _prepareAddFavorite.canProceed,
      defaultTitle = _prepareAddFavorite.defaultTitle;
    if (!canProceed) {
      return;
    }
    setCustomTitle(defaultTitle);
    setShowTitlePrompt(true);
  };
  let starTitle = 'Add to favorites';
  if (loading) {
    starTitle = 'Loading favorites';
  } else if (isFavorited) {
    starTitle = 'Remove from favorites';
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    size: size,
    onClick: handleToggleFavorite,
    "data-testid": "t-button-favorites",
    title: starTitle
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "star",
    weight: isFavorited ? 'fas' : 'far'
  })), /*#__PURE__*/React.createElement(FavoriteTitlePromptModal, {
    isOpen: showTitlePrompt,
    title: customTitle,
    onTitleChange: setCustomTitle,
    onSubmit: handleTitleSubmit,
    onCancel: handleTitleCancel,
    inputRef: titleInputRef
  }));
};
FavoriteButton.propTypes = {
  notificationActions: PropTypes.shape({
    info: PropTypes.func,
    error: PropTypes.func
  }),
  form: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        title: PropTypes.string
      })
    }),
    pendingChanges: PropTypes.bool
  }),
  size: PropTypes.string
};
export { FavoriteButton as UncomposedFavoriteButton };
export default entityComposition(FavoriteButton, ['form'], {}, ['notification']);